// API client factory for `@onxite/plan-sdk`. The host app injects its token
// retrieval (e.g. Zustand store getter) so the SDK never owns auth state.
//
// Why a factory rather than a global axios instance:
//   • Each app can compose interceptors however it wants (refresh, retry, etc.)
//   • Tests can spin up a clean client per case.
//   • Multi-tenant tooling (Storybook MSW) can swap the baseURL trivially.

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export interface PlanApiClientOptions {
  baseURL: string;
  getToken: () => string | null | undefined;
  /** Optional path prefix override (default '/api/v1') */
  prefix?: string;
  /** Hook for telemetry on PLAN_LIMIT_EXCEEDED / FEATURE_NOT_PURCHASED */
  onPlanError?: (err: any) => void;
}

export type PlanApiClient = AxiosInstance & {
  /** Convenience: the resolved base URL the SDK uses for `/plans/*` etc. */
  __planSdkBase: string;
};

export function createPlanApiClient(
  options: PlanApiClientOptions,
): PlanApiClient {
  const prefix = options.prefix ?? '/api/v1';
  const baseURL = `${options.baseURL.replace(/\/$/, '')}${prefix}`;

  const instance = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
  }) as PlanApiClient;

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = options.getToken();
    if (token && config.headers) {
      (config.headers as Record<string, string>).Authorization =
        `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    (err) => {
      const code = err?.response?.data?.code;
      if (
        options.onPlanError &&
        (code === 'PLAN_LIMIT_EXCEEDED' || code === 'FEATURE_NOT_PURCHASED')
      ) {
        options.onPlanError(err);
      }
      return Promise.reject(err);
    },
  );

  instance.__planSdkBase = baseURL;
  return instance;
}
